import { useState } from 'react';

import { type SubmitHandler, useForm } from 'react-hook-form';

import { usePaymentInputs } from 'react-payment-inputs';
import images, { CardImages } from 'react-payment-inputs/images';
import { useShallow } from 'zustand/react/shallow';

import { CardDetailsItem } from '@/entities/card';

import { SafeIcon } from '@/shared/assets';
import { CARD_ERROR_MESSAGES, COLORS, CommonError, NNBSP } from '@/shared/lib';
import {
  Button,
  DashboardFormMessage,
  Form,
  FormField,
  FormItem,
  SwitchWithLabel,
  Typography,
} from '@/shared/ui';

import { AddMoneyFormData } from '../../../model/schema';
import { useAddMoney } from '../../../model/useAddMoney';

const cardImages = images as unknown as CardImages;
export const CreditCardContent = () => {
  const {
    amount,
    fee,
    setStep,
    account,
    setCardNumber,
    setCardExpiry,
    setCardCvv,
    resetCardInfo,
    cardNumber,
    cardExpiry,
    cardCvv,
  } = useAddMoney(useShallow((state) => state));
  const [isLoading, setIsLoading] = useState(false);
  const {
    meta,
    getCardNumberProps,
    getExpiryDateProps,
    getCVCProps,
    getCardImageProps,
  } = usePaymentInputs({ errorMessages: CARD_ERROR_MESSAGES });
  const cardType = meta.cardType as typeof meta.cardType | undefined;
  const form = useForm<AddMoneyFormData>({
    // resolver: zodResolver(addMoneySchema),
    defaultValues: {
      cardNumber,
      expirationDate: cardExpiry,
      cvv: cardCvv,
      amount: amount + fee,
    },
  });
  const simulateSubmission = (): Promise<CommonError> => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const random = Math.random();
        if (random < 0.33) {
          reject(new Error('Insufficient funds'));
        } else if (random < 0.66) {
          reject(new Error());
        } else {
          resolve({ status: 'success', message: 'Payment successful' });
        }
      }, 2000);
    });
  };

  const onSubmit: SubmitHandler<AddMoneyFormData> = async (data) => {
    if (meta.erroredInputs.cardNumber) {
      form.setError('cardNumber', {
        type: 'manual',
        message: meta.erroredInputs.cardNumber,
      });
    }
    if (meta.erroredInputs.expiryDate) {
      form.setError('expirationDate', {
        type: 'manual',
        message: meta.erroredInputs.expiryDate,
      });
    }
    if (meta.erroredInputs.cvc) {
      form.setError('cvv', {
        type: 'manual',
        message:
          meta.erroredInputs.cvc === CARD_ERROR_MESSAGES.invalidCVC
            ? `${cardType?.code.name ?? 'CVV'} is invalid`
            : `Enter a ${cardType?.code.name ?? 'CVV'}`,
      });
    }
    if (
      Object.keys(form.formState.errors).length === 0 &&
      !Object.values(meta.erroredInputs).some(Boolean)
    ) {
      setCardNumber(data.cardNumber);
      setCardExpiry(data.expirationDate);
      setCardCvv(data.cvv);
      try {
        setIsLoading(true);
        await simulateSubmission();
        resetCardInfo();
        setIsLoading(false);
        setStep('success');
      } catch (error: unknown) {
        setIsLoading(false);
        const knownError = error as CommonError;
        if (!knownError.message) {
          setStep('error');
        }
        if (knownError.message === 'Insufficient funds') {
          setStep('no_funds');
        }
      }
    }
  };

  const cvvLength = cardType?.code.length ?? 3;

  return (
    <div className="card-input flex h-[528px] flex-col items-center">
      <div className="flex items-center gap-1 py-1">
        <SafeIcon color={COLORS['icon-brand-500']} />
        <Typography variant="14-r" color="brand-500">
          High-level security
        </Typography>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-grow flex-col"
        >
          <div className="mt-5 flex w-full flex-col gap-1">
            <FormField
              control={form.control}
              name="cardNumber"
              render={({ field }) => (
                <FormItem>
                  <CardDetailsItem
                    value={field.value}
                    autoFocus
                    inputProps={getCardNumberProps({
                      onChange: field.onChange,
                      onBlur: field.onBlur,
                    })}
                    label="Card number"
                    showCardIcon
                    svgProps={getCardImageProps({ images: cardImages })}
                    foundName={cardType?.displayName}
                  />
                  <DashboardFormMessage>
                    {form.formState.errors.cardNumber?.message}
                  </DashboardFormMessage>
                </FormItem>
              )}
            />
            <div className="flex gap-1">
              <FormField
                control={form.control}
                name="expirationDate"
                render={({ field }) => (
                  <FormItem>
                    <CardDetailsItem
                      value={field.value}
                      inputProps={getExpiryDateProps({
                        onChange: field.onChange,
                        onBlur: field.onBlur,
                      })}
                      label="Expiration date"
                      placeholder="MM/YY"
                    />
                    <DashboardFormMessage>
                      {form.formState.errors.expirationDate?.message}
                    </DashboardFormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cvv"
                render={({ field }) => (
                  <FormItem>
                    <CardDetailsItem
                      value={field.value}
                      inputProps={getCVCProps({
                        onChange: field.onChange,
                        onBlur: field.onBlur,
                      })}
                      label={cardType?.code.name ?? 'CVV'}
                      placeholder={`${cvvLength.toString()} digits`}
                      maxLength={cvvLength || 3}
                    />
                    <DashboardFormMessage>
                      {form.formState.errors.cvv?.message}
                    </DashboardFormMessage>
                  </FormItem>
                )}
              />
            </div>
          </div>
          <FormField
            control={form.control}
            name="save_card"
            render={({ field }) => (
              <FormItem>
                <SwitchWithLabel
                  label="Save this card"
                  className="mt-5"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormItem>
            )}
          />
          <div className="mt-auto flex flex-col gap-y-6">
            <Typography
              variant="14-r"
              color="text-base-100"
              className="text-center"
            >
              Including a {fee}
              {NNBSP}
              {account?.symbol} fee
            </Typography>
            <Button
              type="submit"
              size="lg"
              variant="brand-500"
              className="w-full"
              loading={isLoading}
            >
              Pay {amount + fee}&#8239;{account?.symbol}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
